import {
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { DialogPanelComponentContent } from '@toro/common/shared/models/dialogs/DialogPanelComponentContent';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, finalize, map, takeWhile, tap } from 'rxjs/operators';
import { PixDepositService } from '../../services/pix-deposit/pix-deposit.service';
import * as moment from 'moment';
import { Store } from '@ngrx/store';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { environment } from '@hub-workspace/apps/logged-home/src/environments/environment';
import { isCnpj } from '@toro/common/shared/helpers/utils/document';

@Component({
    selector: 'dialog-deposit',
    templateUrl: './dialog-deposit.component.html',
    styleUrls: ['./dialog-deposit.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DialogDepositComponent extends DialogPanelComponentContent implements OnInit, OnDestroy {
    @ViewChild("editValueField", { static: false }) editField: ElementRef;

    public depositForm: FormGroup;
    public codePixLoading: boolean;
    public pixCode: string;
    public timeRemaining$: Observable<number>;
    public isPixTimeExpired: boolean;
    public copyButtonLabel: string;
    public isPixOutOfTime: boolean;
    public hasError: boolean;
    public isPixLoading: boolean = true;
    public isNewBrand: boolean = environment.isNewBrand

    constructor(
        public dialogReference: MatDialogRef<DialogDepositComponent>,
        public pixDepositService: PixDepositService,
        private renderer: Renderer2,
        private formBuilder: FormBuilder,
        private dialogsService: DialogsService,
        private store: Store<any>,
        element: ElementRef,
        @Inject(MAT_DIALOG_DATA) public data,
    ) {
        super(element, true);
        this.depositForm = this.formBuilder.group({
            depositValue: ['', Validators.required],
        });

        this.verifyPixTime();
    }

    public ngOnInit() {
        if(this.data.category == 'pix') {
            this.sendTracking("Page Loaded", {
                'Product Area Origin': 'Depósito',
                'Time Responsável': "Roebling",
                'Step': 'Valor do depósito',
            });
        } else if (this.data.category == 'ted') {
            this.sendTracking("Page Loaded", {
                'Product Area Origin': 'Depósito',
                'Time Responsável': "Roebling",
                'Scenario': 'Depósito via TED',
            });
        }

        super.ngOnInit();
    }

    public ngOnDestroy() { }

    /**
     * Indica se a conta logada é Pessoa Jurídica (documento com mais de 11 dígitos = CNPJ)
     */
    public get isPj(): boolean {
        return isCnpj(this.data && this.data.user && this.data.user.user && this.data.user.user.cpfcnpj);
    }

    public closeMenu() {
        super.close(this.dialogReference, null);
        this.renderer.removeClass(document.body, 'overflow--hidden');
    }

    public generatePixCode(stepper: MatStepper) {
        const formattedCblc = this.data.userAuth.currentUser.cblc.replace(/-.*/, '');
        this.codePixLoading = true;

        this.pixDepositService.getDepositByPixEnabled().then((remoteConfigValue) => {
            if (remoteConfigValue) {
                this.pixDepositService.getPixCode({
                    cblc: formattedCblc,
                    cpf: this.data.user.user.cpfcnpj,
                    value: +this.depositForm.controls.depositValue.value,
                }).pipe(
                    tap((res) => {
                        this.pixCode = res.data.payload;
                        this.codePixLoading = false;
                        this.isPixTimeExpired = false;
                        this.hasError = false;
                        this.copyButtonLabel = 'Copiar código';

                        this.timeRemaining$ = timer(0, 1000).pipe(
                            map(n => (+res.data.expirationInSeconds - n) * 1000),
                            takeWhile(n => n >= 0),
                            tap(timer => {
                                if(timer == 0) {
                                    this.isPixTimeExpired = true;
                                }
                            }),
                        );

                        stepper.next()

                        this.sendTracking("Page Loaded", {
                            'Product Area Origin': 'Depósito',
                            'Time Responsável': "Roebling",
                            'Step': 'QR code gerado',
                            'Value': +this.depositForm.controls.depositValue.value,
                        });
                    }),
                    catchError((err) => {
                        return this.apiErrorPath(stepper);
                    })
                ).subscribe();
            } else {
                return this.apiErrorPath(stepper);
            }
        })

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão continuar',
            'Step': 'Revisão Pix',
            'Value': +this.depositForm.controls.depositValue.value,
        });
    }

    public allowToChangeDepositValue(): void {
        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão continuar',
            'Step': 'Valor de depósito',
            'Value': +this.depositForm.controls.depositValue.value,
        });

        this.depositForm.patchValue({
            depositValue: +this.depositForm.controls.depositValue.value,
        });

        this.sendTracking("Page Loaded", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Step': 'Revisão pix',
        });
    }

    public copyToClipboard(item) {
        this.copyButtonLabel = 'Código copiado';
        navigator.clipboard.writeText(item);

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão copiar código',
            'Step': 'QR code gerado',
            'Value': +this.depositForm.controls.depositValue.value,
        });
    }

    public openDepositModal(depositCategory: string) {
        this.dialogsService.showComponentAsAsidePanel(
            DialogDepositComponent,
            true,
            {
                category: depositCategory,
                user: this.data.user,
                userAuth: this.data.userAuth,
            }
        );
    }

    public changeDepositMethod(depositMethod: string) {
        this.closeMenu();
        this.openDepositModal(depositMethod);

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão transferir via TED',
            'Status': 'Fora do horário',
        });
    }

    public verifyPixTime() {
      this.pixDepositService.getDepositExecutionDate().pipe(
          tap((res) => {
              this.isPixOutOfTime = !res.data.willBeProcessedToday;

              if(this.isPixOutOfTime) {
                  this.sendTracking("Page Loaded", {
                      'Product Area Origin': 'Depósito',
                      'Time Responsável': "Roebling",
                      'Status': 'Feedback fora do horário pix',
                  });
              }
          }),
          catchError((err) => {
              this.isPixOutOfTime = true;
              return EMPTY;
          }),
          finalize(() => this.isPixLoading = false)
      )
      .subscribe();
    }

    public sendTracking(event: string, breakdowns: any) {
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: { type: event, events: breakdowns },
        });
    }

    public backStepAfterExpire(stepper: MatStepper) {
        stepper.previous();

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão voltar',
            'Step': 'QR code gerado',
            'Scenario': 'QR code expirado',
            'Value': +this.depositForm.controls.depositValue.value,
        });
    }

    public backStepAfterError(stepper: MatStepper) {
        stepper.previous();

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Element': 'Botão tentar novamente',
            'Status': 'Feedback erro',
            'Scenario': 'Feedback erro',
            'Value': +this.depositForm.controls.depositValue.value,
        });
    }

    public focusField() {
        this.editField.nativeElement.focus();
    }

    public apiErrorPath(stepper: MatStepper) {
        this.sendTracking("Page Loaded", {
            'Product Area Origin': 'Depósito',
            'Time Responsável': "Roebling",
            'Status': 'Feedback erro',
            'Value': +this.depositForm.controls.depositValue.value,
        });

        this.codePixLoading = false;
        this.isPixTimeExpired = false;
        this.hasError = true;

        stepper.next();

        return EMPTY;
    }
}
